/**
 * Auth callback — /auth/callback
 *
 * Microsoft redirects the browser here after successful sign-in with
 * `?code=…&state=…` in the URL. We:
 *
 *   1. Let MSAL process the redirect (verifying state, etc.)
 *   2. Hand the code + PKCE verifier to our backend at /api/auth/callback
 *   3. The backend exchanges the code with Microsoft, upserts the user,
 *      issues an httpOnly session cookie, and returns our user DTO
 *   4. We refetch /api/auth/me to hydrate the auth store and redirect to
 *      the dashboard (or the page the user originally wanted)
 *
 * Failures show an explanatory card with a "Try again" button that
 * returns the user to /login.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { msalInstance, handleRedirectResponse } from '@/auth/msal';
import { apiRequest } from '@/api/client';
import { useAuthStore } from '@/auth/auth-store';
import { AxisFinanceLogo } from '@/acc-ui/components/AxisFinanceLogo';
import { Orbs } from '@/acc-ui/components/Orbs';

type Phase = 'processing' | 'success' | 'error';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const [phase, setPhase] = useState<Phase>('processing');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        // SPA flow: MSAL.js completes the authorization-code-with-PKCE
        // exchange against Microsoft itself and gives us back tokens.
        // We forward the id_token to our backend, which verifies the
        // signature against Microsoft's JWKS and starts a session.
        //
        // Why this approach: the original design called for the backend
        // to do confidential-client code exchange (with the client
        // secret), but the Azure AD app is now registered as an SPA —
        // SPAs can't use secrets, so MSAL.js does the exchange.

        // Clear the stashed OAuth fragment that App.tsx might have set
        // before navigating here — we don't need it for the id_token flow.
        try {
          sessionStorage.removeItem('loanaudit.oauth.fragment');
        } catch { /* ignore */ }

        // handleRedirectResponse() processes Microsoft's redirect AND
        // returns the AuthenticationResult containing idToken/accessToken.
        // If we got here via App.tsx's OAuth detector, MSAL already
        // processed the redirect on a previous tick (it does so as part
        // of initMsal); calling again returns null.
        let result = await handleRedirectResponse();

        // If null (already-processed or direct visit), try to recover the
        // id_token from the active account's cache.
        if (!result || !result.idToken) {
          const account = msalInstance.getAllAccounts()[0];
          if (account) {
            try {
              const silent = await msalInstance.acquireTokenSilent({
                scopes: ['User.Read', 'profile', 'openid', 'email'],
                account,
              });
              result = silent;
            } catch {
              /* fall through */
            }
          }
        }

        if (!result || !result.idToken) {
          throw new Error('Authentication did not produce an id_token. Please try signing in again.');
        }

        // Hand the id_token to the backend.
        await apiRequest('/api/auth/id-token', {
          method: 'POST',
          body: {
            idToken: result.idToken,
          },
        });

        await fetchMe();
        setPhase('success');
        setTimeout(() => navigate('/', { replace: true }), 300);
      } catch (err) {
        const msg =
          err instanceof Error && err.message
            ? err.message
            : 'Something went wrong during sign-in.';
        setErrorMsg(msg);
        setPhase('error');
        try {
          msalInstance.clearCache();
        } catch {
          /* ignore */
        }
      }
    })();
  }, [fetchMe, navigate]);

  return (
    <div className="relative min-h-[100dvh] flex items-center justify-center px-4">
      <Orbs variant="login" />
      <div
        className="relative z-10 liquid-glass-strong rounded-[clamp(20px,3vw,28px)] w-full"
        style={{ maxWidth: 400, padding: 'clamp(32px, 4vw, 48px) clamp(24px, 3vw, 36px)' }}
      >
        <div className="flex flex-col items-center text-center">
          <AxisFinanceLogo height={36} />
          {phase === 'processing' && (
            <>
              <h1 className="mt-5 text-title font-semibold text-text-primary">
                Finishing sign-in…
              </h1>
              <p className="mt-1 text-label text-text-tertiary">
                Completing handshake with Microsoft
              </p>
            </>
          )}
          {phase === 'success' && (
            <>
              <h1 className="mt-5 text-title font-semibold text-emerald-700">
                Signed in
              </h1>
              <p className="mt-1 text-label text-text-tertiary">
                Taking you to your dashboard…
              </p>
            </>
          )}
          {phase === 'error' && (
            <>
              <h1 className="mt-5 text-title font-semibold text-red-700">
                Sign-in failed
              </h1>
              <p className="mt-1 text-label text-text-tertiary">{errorMsg}</p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="mt-5 w-full min-h-11 px-4 py-3 rounded-[14px] bg-[linear-gradient(135deg,#97144D_0%,#5E0C30_100%)] text-white text-body font-medium shadow-cta hover:shadow-cta-hover hover:-translate-y-[1px] transition-all"
              >
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
